import * as React from "react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import Portal from "../Popups/Portal";
import classnames from "classnames";
import { config } from "../Map/config";
import { Feature } from "ol";
import { updateMapSize } from "../Providers/mapProvider";
import { useNomaContext } from "../Contexts/MapContext";
import { useFiltersContext } from "../Contexts/FiltersContext";
import { useCustomViewContext } from "../Contexts/CustomViewContext";
import { IRelatedDocs } from "../interfaces";
import ErrorImageDark from "../img/error-img-dark.svg";
import { ImageLoadingState } from "../Map/enums";
import "./CustomView.scss";

interface ICustomViewProps {
    features: Feature[];
    onLocateClick: (feature: Feature) => void;
}

const minHeight = 45;

export const CustomView: FC<ICustomViewProps> = (props) => {
    const [height, setHeight] = useState(300);

    const moveEndListener = useRef(null);

    const { isMobile, isIFrame } = useNomaContext();
    const { setExtent } = useFiltersContext();

    const {
        hidden,
        setHidden,
        setTabs,
        setFieldNames,
        setTabData,
        setRelatedDocs,
        moveEndTime,
        setMoveEndTime,
        setImages
    } = useCustomViewContext();

    useEffect(() => {
        setTimeout(() => updateMapSize(), 0);
        !hidden && setHidden(false);
    }, [hidden]);

    useEffect(() => {
        if (props.features) {
            setTabs();
            setFieldNames();
            setExtent();
            setImages(props.features);
            setTabData(props.features);
            setRelatedDocs(props.features);
            setTimeout(() => updateMapSize(), 0);
        }
    }, [props.features]);

    useEffect(() => {
        !moveEndListener.current && document.addEventListener("moveend", () => setMoveEndTime(Date.now()));

        // Adjust height on screen orientation change
        const landscapeMediaQuery = window.matchMedia("(min-width: 481px) and (max-height: 481px)");
        const isLandscape = (landscapeMediaQuery: any) =>
            landscapeMediaQuery.matches ? setHeight(document.body.clientHeight * 0.5) : setHeight(300);
        isLandscape(landscapeMediaQuery);
        landscapeMediaQuery.addListener(isLandscape);
    }, []);

    useEffect(() => {
        setExtent();
    }, [moveEndTime]);

    useEffect(() => {
        updateMapSize();
    }, [height]);

    return (
        <div className="custom-view-container shadow">
            <ShowButton></ShowButton>
            {!hidden ? (
                <div className="cv-view" style={{ height: height + "px" }}>
                    <DragButton height={height} setHeight={setHeight}></DragButton>
                    <ViewHeader></ViewHeader>
                    <div className="cv-content">
                        {!isMobile && !isIFrame && <ContentHeader></ContentHeader>}
                        {isMobile || isIFrame ? (
                            <MobileContentBody></MobileContentBody>
                        ) : (
                            <ContentBody onLocateClick={props.onLocateClick}></ContentBody>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

/********************************************************************************/
/********                 ShowButton                                     ********/
/********************************************************************************/
const ShowButton = () => {
    const { setHidden } = useCustomViewContext();

    return (
        <Portal id={"custom-view-show-button"}>
            <div
                className="cv-show-button zemesnoma-map-button ol-unselectable ol-control"
                onClick={() => setHidden(false)}
            >
                <span className="icon-zemesnoma-chevron-up"></span>
            </div>
        </Portal>
    );
};

/********************************************************************************/
/********                 DragButton                                     ********/
/********************************************************************************/

const DragButton = ({ height, setHeight }: { height: number; setHeight: (height: number) => void }) => {
    const { isMobile, isIFrame } = useNomaContext();

    const maxHeight =
        document.body.clientHeight > 480 ? document.body.clientHeight - 260 : document.body.clientHeight * 0.6;

    const onMouseDown = useCallback(
        (e) => {
            const startPos = e.clientY;
            const onMouseMove = (event: MouseEvent) => {
                const newHeight = height - event.clientY + startPos;
                setHeight(newHeight >= minHeight ? (newHeight >= maxHeight ? maxHeight : newHeight) : minHeight);
            };
            const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            };
            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        },
        [height, maxHeight, minHeight]
    );

    const onTouchStart = useCallback(
        (e) => {
            const startPos = e.touches[0].clientY;
            const onTouchMove = (event: TouchEvent) => {
                const newHeight = height - event.touches[0].clientY + startPos;
                setHeight(newHeight >= minHeight ? (newHeight >= maxHeight ? maxHeight : newHeight) : minHeight);
            };
            const onTouchUp = () => {
                document.removeEventListener("touchmove", onTouchMove);
                document.removeEventListener("touchend", onTouchUp);
            };
            document.addEventListener("touchmove", onTouchMove);
            document.addEventListener("touchend", onTouchUp);
        },
        [height, maxHeight, minHeight]
    );

    const button = (
        <div className="cv-drag-button">
            <svg height="8" width="30">
                <line x1="2" y1="1" x2="28" y2="1"></line>
                <line x1="2" y1="5" x2="28" y2="5"></line>
            </svg>
        </div>
    );

    return isMobile && !isIFrame ? (
        <div className="cv-drag" onTouchStart={(e) => onTouchStart(e)}>
            {button}
        </div>
    ) : (
        <div className="cv-drag" onMouseDown={(e) => onMouseDown(e)}>
            {button}
        </div>
    );
};

/********************************************************************************/
/********                 ViewHeader                                     ********/
/********************************************************************************/
const ViewHeader = () => {
    const { tabNames, setHidden, changeTab } = useCustomViewContext();
    const { activeTab, setZoomToSourceFeatures } = useNomaContext();

    const onChangeTab = (tabCode) => {
        setZoomToSourceFeatures(1);
        changeTab(tabCode);
    };

    return (
        <div className="cv-header">
            {tabNames && tabNames.length
                ? tabNames.map((tab) => (
                      <div
                          className={classnames("cv-button unselectable", {
                              "cv-button-active": tab.code === activeTab
                          })}
                          key={"tab=" + tab.name}
                          onClick={() => onChangeTab(tab.code)}
                      >
                          {tab.name}
                      </div>
                  ))
                : null}
            <div className="cv-minimize-button" onClick={() => setHidden(true)}>
                <span className="icon-zemesnoma-chevron-down"></span>
            </div>
        </div>
    );
};

/********************************************************************************/
/********                 ContentHeader                                  ********/
/********************************************************************************/
const ContentHeader = () => {
    const { tabConfig, fieldNames } = useCustomViewContext();
    const { getFieldAlias } = useNomaContext();

    return (
        <div className="cv-content-header">
            <div className="cv-row">
                <div className="cv-cell" key={"openButtonFieldName"}>
                    {config.dataView.openButtonFieldName || "openButtonFieldName"}
                </div>
                <div className="cv-cell" key={"imageFieldName"}>
                    {config.dataView.imageFieldName || "imageFieldName"}
                </div>
                {fieldNames.map((fieldName, i) => (
                    <div className="cv-cell" key={fieldName + "title" + i}>
                        {getFieldAlias(fieldName)}
                    </div>
                ))}
                {tabConfig?.showDocs && (
                    <div className="cv-cell" key={"docsButtonFieldName"}>
                        {config.dataView.docsButtonFieldName || "docsButtonFieldName"}
                    </div>
                )}
                {tabConfig?.showApply && (
                    <div className="cv-cell cv-apply-header" key={"applyButtonFieldName"}>
                        {config.dataView.applyButtonFieldName || "applyButtonFieldName"}
                    </div>
                )}
            </div>
        </div>
    );
};

/********************************************************************************/
/********                 ContentBody                                    ********/
/********************************************************************************/
const ContentBody = ({ onLocateClick }: { onLocateClick: (feature: Feature) => void }) => {
    const { tabConfig, tabData, fieldNames } = useCustomViewContext();
    const {
        getFieldValue,
        setPreviewFeature,
        setDownloadsPopupDocs,
        setApplyFeature,
        setRegisterFeature,
        setSideViewVisible,
        setLayersMenuVisible
    } = useNomaContext();

    const onClickApply = (feature: Feature) => {
        const isAuction = feature.get("TYPEOFLEASEGRANTING") !== 1 && feature.get("TYPEOFLEASEGRANTING") !== 5;
        isAuction ? setApplyFeature({ feature, isRight: true }) : setRegisterFeature(feature);
        setSideViewVisible(false);
        setLayersMenuVisible(false);
    };

    return (
        <div className="cv-content-data">
            {tabData && tabData.length
                ? tabData.map((feature: Feature, i: number) => (
                      <div key={"cv-row" + i} className="cv-row">
                          <div className="cv-cell cv-open" key={"cv-open" + i}>
                              <span className="icon-zemesnoma-open" onClick={() => setPreviewFeature(feature)}></span>
                          </div>
                          <HoverableImage id={feature.get("OBJECTID")} key={"hoverable-" + i}></HoverableImage>
                          {fieldNames.map((fieldName, j) =>
                              fieldName === "OBJECTNAME" ? (
                                  <div
                                      className="cv-cell cv-locate"
                                      key={feature.get("OBJECTID") + "cell" + j}
                                      onClick={() => onLocateClick(feature)}
                                  >
                                      {getFieldValue(feature, fieldName)}
                                  </div>
                              ) : (
                                  <div className="cv-cell" key={feature.get("OBJECTID") + "cell" + j}>
                                      {getFieldValue(feature, fieldName)}
                                  </div>
                              )
                          )}
                          {tabConfig?.showDocs && (
                              <DocsButton feature={feature} onClick={setDownloadsPopupDocs}></DocsButton>
                          )}
                          {tabConfig?.showApply && <ApplyButton feature={feature} onClick={onClickApply}></ApplyButton>}
                      </div>
                  ))
                : null}
        </div>
    );
};
/********************************************************************************/
/********               MOBILE ContentBody                               ********/
/********************************************************************************/
const MobileContentBody = () => {
    const { tabConfig, tabData } = useCustomViewContext();
    const { getFieldValue, setIdentifiedFeature } = useNomaContext();

    return (
        <div className="cv-content-data">
            {tabData && tabData.length
                ? tabData.map((feature: Feature, i: number) => (
                      <div className="cv-row" key={"cv-row" + i} onClick={() => setIdentifiedFeature(feature)}>
                          <HoverableImage id={feature.get("OBJECTID")} key={"hoverable-" + i}></HoverableImage>
                          {tabConfig.mobileUIFields.slice(0, 1).map((fieldName, j) =>
                              fieldName === "OBJECTNAME" ? (
                                  <div className="cv-cell cv-locate" key={feature.get("OBJECTID") + "cell" + j}>
                                      {getFieldValue(feature, fieldName)}
                                  </div>
                              ) : (
                                  <div className="cv-cell" key={feature.get("OBJECTID") + "cell" + j}>
                                      {getFieldValue(feature, fieldName)}
                                  </div>
                              )
                          )}
                          <div className="cv-cell" key={feature.get("OBJECTID") + "column-cell"}>
                              {tabConfig.mobileUIFields.slice(1).map((fieldName, k) => (
                                  <div className="column-cell" key={feature.get("OBJECTID") + "cc-element" + k}>
                                      {getFieldValue(feature, fieldName)}
                                  </div>
                              ))}
                          </div>
                      </div>
                  ))
                : null}
        </div>
    );
};

/********************************************************************************/
/********                 HoverableImage                                 ********/
/********************************************************************************/
const HoverableImage = ({ id }: { id: number }) => {
    if (!id) {
        return;
    }
    const { images } = useCustomViewContext();

    const imgUrl = images.find((img) => img.objectId === id);
    const url = imgUrl
        ? imgUrl.url === ImageLoadingState.NoImages
            ? ErrorImageDark
            : imgUrl.url
        : ImageLoadingState.Loading;

    return (
        <div className="cv-cell cv-image" key={"cv-image"}>
            {url === ImageLoadingState.Loading ? (
                <span className="icon-zemesnoma-spinner rotating"></span>
            ) : (
                <img className="thumbnail" src={url} key={"cv-thumbnail"} loading="lazy"></img>
            )}
            {url !== ErrorImageDark && url !== ImageLoadingState.Loading && (
                <div className="image-container">
                    <div className="image-positioning">
                        <img className="original" src={url} key={"cv-original"} loading="lazy"></img>
                    </div>
                </div>
            )}
        </div>
    );
};

/********************************************************************************/
/********                 DocsButton                                     ********/
/********************************************************************************/
export const DocsButton = ({ feature, onClick }: { feature: Feature; onClick: CallableFunction }) => {
    const { relatedDocs } = useCustomViewContext();

    const objectId = feature.get("OBJECTID");
    let objectDocs = relatedDocs.find((item: IRelatedDocs) => (item.objectId === objectId ? item.docs : undefined));

    useEffect(() => {
        objectDocs = relatedDocs.find((item: IRelatedDocs) => (item.objectId === objectId ? item.docs : undefined));
    }, [relatedDocs]);

    return (
        <div className="cv-cell cv-docs" key={"cv-docs" + objectId}>
            <span
                className={classnames("docs-icon icon-zemesnoma-menu-reports", { disabled: !objectDocs })}
                onClick={() => onClick(objectDocs)}
            ></span>
        </div>
    );
};

/********************************************************************************/
/********                 ApplyButton                                     ********/
/********************************************************************************/
export const ApplyButton = ({ feature, onClick }: { feature: Feature; onClick: CallableFunction }) => {
    const end = feature.get("APPLICATIONENDDATE");
    const endDate = end && new Date(end);
    const endDateLastHour = endDate && endDate.setHours(24, 0, 0, 0);

    const canNotApply = endDateLastHour && Date.now() >= endDateLastHour;

    return (
        <div className="cv-cell cv-apply">
            <button className={classnames("apply-button", { disabled: canNotApply })} onClick={() => onClick(feature)}>
                <span className="apply-icon icon-zemesnoma-forward"></span>
                <span>Pieteikties</span>
                {canNotApply ? <div className="hover-text">Pieteikšanās termiņš beidzies</div> : null}
            </button>
        </div>
    );
};
